import type { PostgrestError } from '@supabase/supabase-js';
import { supabase } from './supabaseClient';
import type { VocabWord } from '../types/vocab';

interface FlashcardWordRow {
  id: string;
  word: string;
  definition: string;
  similar_words_category?: string | null;
  similar_words?: string | null;
  source?: string | null;
}

const PAGE_SIZE = 500;

const mapRowToVocabWord = (row: FlashcardWordRow): VocabWord => ({
  Word: row.word || '',
  Definition: row.definition || '',
  'Similar words category': row.similar_words_category ?? '',
  'Similar words ': row.similar_words ?? '',
  ID: row.id,
  source: row.source ?? 'core',
  isCustom: false
});

const selectColumns = 'id, word, definition, similar_words_category, similar_words';

export async function fetchVocabRange(
  from = 0,
  to = PAGE_SIZE - 1
): Promise<{ data: VocabWord[]; error: PostgrestError | null }> {
  const { data, error } = await supabase
    .from('flashcard_words')
    .select(selectColumns)
    .order('word', { ascending: true })
    .range(from, to);

  return {
    data: data?.map(mapRowToVocabWord) ?? [],
    error: error ?? null
  };
}

// Module-level singleton: only one paginated fetch runs at a time.
// Concurrent callers (e.g. React StrictMode double-invoke) share the same
// promise so two while-loops never race each other during OTP token exchange.
let _wordsCache: VocabWord[] | null = null;
let _fetchPromise: Promise<{ data: VocabWord[]; error: PostgrestError | null }> | null = null;

async function _paginatedFetch(pageSize: number): Promise<{ data: VocabWord[]; error: PostgrestError | null }> {
  const aggregated: VocabWord[] = [];
  let page = 0;

  while (true) {
    const from = page * pageSize;
    const to = from + pageSize - 1;
    const { data, error } = await supabase
      .from('flashcard_words')
      .select(selectColumns)
      .order('word', { ascending: true })
      .range(from, to);

    if (error) return { data: aggregated, error };
    if (!data || data.length === 0) break;
    aggregated.push(...data.map(mapRowToVocabWord));
    if (data.length < pageSize) break;
    page += 1;
  }

  return { data: aggregated, error: null };
}

export function fetchAllVocabWords(
  pageSize = PAGE_SIZE
): Promise<{ data: VocabWord[]; error: PostgrestError | null }> {
  if (_wordsCache) return Promise.resolve({ data: _wordsCache, error: null });
  if (_fetchPromise) return _fetchPromise;

  _fetchPromise = _paginatedFetch(pageSize)
    .then(result => {
      if (!result.error) _wordsCache = result.data;
      _fetchPromise = null;
      return result;
    })
    .catch(err => {
      console.error('[wordBank] _paginatedFetch threw:', err);
      _fetchPromise = null; // allow retry on next call
      throw err;
    });

  return _fetchPromise;
}

